import {
  Development,
  JobsRankingResponse,
  ObjectTokkoFilters,
  ObjectTokkoLocations,
  Property,
  ResponseRankingDevQuery,
  ResponseRankingQuery,
} from 'types'
import { mediacoreApi } from '../mediacoreApi'
import {
  getChildrens,
  getFathers,
  getGrandfathers,
  getOnlyArrayOperationById,
  getSumaryFilterDefault,
  makeParamsCoordinates,
} from '@helpers/helper.filters'
import { removeDuplicateProperties } from '@helpers/helper.properties'
import { preferBaigunTokkoImages } from '@helpers/helper.baigunImages'

interface Params {
  API_KEY?: string
  OPERATION?: string
  TYPE: any
  CTAG?: number[]
  W_CTAG?: number[]
}

export const ownerExtended = mediacoreApi.injectEndpoints({
  endpoints: (builder) => ({
    getNewProperties: builder.query({
      query: ({ params_get, params_post, coordinates, polygonCoordinates, CTAG, W_CTAG }) => ({
        url: `owner/tokko-propiedades/search/${params_get}&client=${process.env.GATSBY_CLIENTID}${CTAG?.length > 0 ? '&custom_tags=' + CTAG : ''}${W_CTAG?.length > 0 ? '&without_custom_tags=' + W_CTAG : ''}`,
        body: { ...makeParamsCoordinates(coordinates, polygonCoordinates), ...params_post },
        method: 'POST',
      }),
      transformResponse: ({ meta, objects }) => {
        return { meta, objects: removeDuplicateProperties(preferBaigunTokkoImages(objects)) }
      },
    }),
    getRanking: builder.query<ResponseRankingQuery[], void>({
      query: () => `owner/externo/tokko-propiedades/ranking/?client=${process.env.GATSBY_CLIENTID}`,
    }),
    getDevRanking: builder.query<ResponseRankingDevQuery[], void>({
      query: () => `owner/externo/tokko-desarrollos/ranking/?client=${process.env.GATSBY_CLIENTID}`,
    }),
    getMediacoreDevRanking: builder.query<ResponseRankingDevQuery[], void>({
      query: () => `site/desarrollos/ranking/?client=${process.env.GATSBY_CLIENTID}`,
    }),
    getJobsRanking: builder.query<JobsRankingResponse[], void>({
      query: () => `jobs/posiciones/ranking/?client=${process.env.GATSBY_CLIENTID}`,
    }),
    getDevelopmentsMiddleware: builder.query<Development[], void>({
      query: () => `owner/externo/tokko-desarrollos/?client=${process.env.GATSBY_CLIENTID}`,
      transformResponse: (data: Development[]) => preferBaigunTokkoImages(data),
    }),
    getDevelopmentMiddleware: builder.query<Development, { DEVELOPMENT_ID?: string }>({
      query: ({ DEVELOPMENT_ID }) =>
        `owner/externo/tokko-desarrollos/${DEVELOPMENT_ID}?client=${process.env.GATSBY_CLIENTID}`,
      transformResponse: (data: Development) => preferBaigunTokkoImages(data),
    }),
    getPropertyMiddleware: builder.query<Property, { PROPERTY_ID?: string }>({
      query: ({ PROPERTY_ID }) => {
        const id = PROPERTY_ID ?? ''
        const params = new URLSearchParams({ client: String(process.env.GATSBY_CLIENTID) })
        return `owner/tokko-propiedades/${id}/ficha/?${params.toString()}`
      },
      transformResponse: (data: Property) => preferBaigunTokkoImages(data),
    }),
    getNewUnities: builder.query({
      query: ({ DEVELOPMENT_ID }) => ({
        url: `owner/tokko-propiedades/search/?limit=1000&client=${process.env.GATSBY_CLIENTID}`,
        body: { development_id: DEVELOPMENT_ID },
        method: 'POST',
      }),
      transformResponse: ({ objects }) => {
        return preferBaigunTokkoImages(objects)
      },
    }),
    getTotalFilter: builder.query<ObjectTokkoFilters, Params>({
      query: ({ OPERATION = '', TYPE = [], CTAG = [], W_CTAG = [] }) => ({
        url: `owner/externo/tokko-propiedades/get_search_summary/?client=${
          process.env.GATSBY_CLIENTID
        }${OPERATION ? '&operation_types=' + getOnlyArrayOperationById(OPERATION) : ''}${
          CTAG?.length > 0 ? '&custom_tags=' + CTAG : ''
        }${W_CTAG?.length > 0 ? '&without_custom_tags=' + W_CTAG : []}`,
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const op = queryArgs?.OPERATION ? getOnlyArrayOperationById(queryArgs.OPERATION) : ''
        const tags = (queryArgs?.CTAG ?? []).slice().sort().join(',')
        return `${endpointName}|client=${process.env.GATSBY_CLIENTID}|op=${op}|ctag=${tags}`
      },
    }),
    getLocations: builder.query<ObjectTokkoLocations, Params>({
      query: ({ OPERATION = '', TYPE = [], CTAG = [] }) => ({
        url: `owner/tokko-locations/public/?client=${process.env.GATSBY_CLIENTID}${
          OPERATION ? '&operation_types=' + getOnlyArrayOperationById(OPERATION) : ''
        }${
          TYPE?.length > 0
            ? '&property_types=' + TYPE?.map((element: { name: string; id: string }) => element.id)
            : ''
        }${CTAG?.length > 0 ? '&custom_tags=' + CTAG : ''}`,
        method: 'GET',
      }),

      transformResponse: ({ meta, objects, locations }) => {
        const gfs = getGrandfathers({ objects, locations })
        const result: any = []
        for (const gf of gfs) {
          const data = gf[1]
          const extractData = (obj: any) => {
            Object.entries(obj).forEach(([key, value]: any) => {
              if (key !== 'count' && typeof value === 'object' && value !== null) {
                if (value.id) {
                  result.push({ name: key.replace(/_/g, ' '), id: value.id })
                }
                // Llamada recursiva para explorar más niveles
                extractData(value)
              }
            })
          }
          extractData(data)
        }
        return { objects: result, meta, locations }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetNewPropertiesQuery,
  useGetRankingQuery,
  useGetDevRankingQuery,
  useGetDevelopmentsMiddlewareQuery,
  useGetDevelopmentMiddlewareQuery,
  useGetPropertyMiddlewareQuery,
  useGetNewUnitiesQuery,
  useGetTotalFilterQuery,
  useGetMediacoreDevRankingQuery,
  useGetJobsRankingQuery,
  useGetLocationsQuery,
} = ownerExtended
